/**
 * Feature-detects how this WebKit build reports getBoundingClientRect for
 * elements inside a CSS `zoom:` subtree.
 *
 * WebKit < 26.4 divided the reported rect by the element's effective zoom
 * (a 13-year-old bug, fixed in Safari 26.4 to match the CSS Viewport spec:
 * rects now come back in visual, zoom-scaled coordinates). The canvas
 * sharp-zoom workaround (`.safari-zoom-active`, canvas-viewport.tsx) puts
 * CSS `zoom:` on the React Flow viewport, and the native-glass plate sync
 * (native-glass-collect.ts) must convert reported rects to visual
 * coordinates on engines with the old semantics — otherwise AppKit plates
 * render permanently offset from the DOM content while zoomed.
 *
 * The probe replicates the workaround's structure once, classifies the
 * engine, and caches the result for the lifetime of the page.
 */
import { isTauri } from "@/lib/is-tauri";

export type ZoomRectSemantics = "visual" | "unscaled" | "unknown";

type RectLike = { x: number; y: number; width: number; height: number };

/** Probe geometry. Host sits at a non-zoomed offset so the classifier can
 *  tell "divided by effective zoom" (offsets outside the zoom subtree are
 *  divided too) apart from "scaled about the zoom root". */
export const PROBE_ZOOM = 2;
const HOST_LEFT = 200;
const HOST_TOP = 100;
const ROOT_TX = 80;
const ROOT_TY = 40;
const CHILD_LEFT = 30;
const CHILD_TOP = 10;
const CHILD_WIDTH = 50;
const CHILD_HEIGHT = 20;
const TOLERANCE = 1.5;

/** Where the probe child visually renders, by construction: the zoomed
 *  subtree scales its content by PROBE_ZOOM, and the root's
 *  translate(ROOT_TX / PROBE_ZOOM) is multiplied back by the zoom —
 *  the same math the shipping `.safari-zoom-active` rule relies on. */
export const PROBE_EXPECTED_VISUAL: RectLike = {
  x: HOST_LEFT + ROOT_TX + CHILD_LEFT * PROBE_ZOOM,
  y: HOST_TOP + ROOT_TY + CHILD_TOP * PROBE_ZOOM,
  width: CHILD_WIDTH * PROBE_ZOOM,
  height: CHILD_HEIGHT * PROBE_ZOOM,
};

const near = (a: number, b: number) => Math.abs(a - b) <= TOLERANCE;

const matches = (rect: RectLike, expected: RectLike) =>
  near(rect.x, expected.x) &&
  near(rect.y, expected.y) &&
  near(rect.width, expected.width) &&
  near(rect.height, expected.height);

/** Pure classifier over the measured probe rect — exported for tests. */
export function classifyProbeRects(childRect: RectLike): ZoomRectSemantics {
  if (matches(childRect, PROBE_EXPECTED_VISUAL)) return "visual";
  const rescaled: RectLike = {
    x: childRect.x * PROBE_ZOOM,
    y: childRect.y * PROBE_ZOOM,
    width: childRect.width * PROBE_ZOOM,
    height: childRect.height * PROBE_ZOOM,
  };
  if (matches(rescaled, PROBE_EXPECTED_VISUAL)) return "unscaled";
  return "unknown";
}

let cached: ZoomRectSemantics | null = null;

export function detectZoomRectSemantics(): ZoomRectSemantics {
  if (cached !== null) return cached;
  if (typeof document === "undefined" || !document.body) {
    // Too early to probe — do not cache, retry on next call.
    return "unknown";
  }

  const host = document.createElement("div");
  host.style.cssText = `position:fixed;left:${HOST_LEFT}px;top:${HOST_TOP}px;width:0;height:0;visibility:hidden;pointer-events:none;`;
  const root = document.createElement("div");
  root.style.cssText = `position:absolute;left:0;top:0;width:120px;height:80px;transform:translate(${ROOT_TX / PROBE_ZOOM}px,${ROOT_TY / PROBE_ZOOM}px);zoom:${PROBE_ZOOM};`;
  const child = document.createElement("div");
  child.style.cssText = `position:absolute;left:${CHILD_LEFT}px;top:${CHILD_TOP}px;width:${CHILD_WIDTH}px;height:${CHILD_HEIGHT}px;`;
  root.appendChild(child);
  host.appendChild(root);
  document.body.appendChild(host);
  const childRect = child.getBoundingClientRect();
  host.remove();

  cached = classifyProbeRects(childRect);
  return cached;
}

/**
 * Whether the sharp-zoom CSS `zoom:` path is safe to enable. In plain
 * browsers there are no native plates, so it is always safe; under Tauri it
 * requires rect semantics we know how to compensate in the glass sync.
 */
export function sharpZoomCompatibleWithNativeGlass(): boolean {
  if (!isTauri()) return true;
  return detectZoomRectSemantics() !== "unknown";
}

/** Test-only: clears the cached probe result. */
export function __resetZoomRectSemanticsForTests(): void {
  cached = null;
}
